import { useEffect, useState, ReactElement } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, outlinedInputClasses, TextFieldProps } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Theme, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';

import { Payment } from './Components/PaymentDetails';
import { PaymentsTable } from './PaymentsTable';
import DateRangePicker, { DateRangeValues, NullableDate } from '../../components/DateRangePicker';
import useQuery from '../../hooks/useQuery';
import { supportedCurrencies, currencySelectMenuProps } from '../../lib/currencies';
import { getErrorMessage } from '../../lib/error';
import { ListPayments } from '../../lib/useDeveloperApi';

const statuses = [
  'AUTHORIZATION_REQUIRED',
  'AUTHORIZING',
  'PROCESSING',
  'SUBMITTED',
  'EXECUTED',
  'FAILED',
  'CANCELLED',
  'CREATED',
];
const paymentTypeOptions = [
  { value: 'MANUAL', label: 'Bank transfer (manual)' },
  { value: 'CARD', label: 'Card' },
  { value: 'MANDATE', label: 'Direct debit' },
  { value: 'WALLET', label: 'Wallet' },
];

const getStyles = (name: string, statuses: string[], theme: Theme) => {
  return {
    fontWeight: statuses.indexOf(name) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
  };
};

type paramFilters = {
  dateRangeValue: DateRangeValues;
  status: string[];
  currencies: string[];
  paymentTypes: string[];
  searchText: string;
  offset: number;
};

type LocationState = {
  useDefaultDateFilter: boolean;
};

const getInitialFiltersFromQuery = (query: URLSearchParams, useDefaultDateFilter: boolean): paramFilters => {
  let initialStatus: string[] = [];
  const statusQuery = query.get('status');
  if (statusQuery !== null) {
    initialStatus = statusQuery.split(',');
  }

  let initialCurrencies: string[] = [];
  const currenciesQuery = query.get('currencies');
  if (currenciesQuery !== null) {
    initialCurrencies = currenciesQuery.split(',');
  }

  let initialPaymentTypes: string[] = [];
  const paymentTypesQuery = query.get('payment_types');
  if (paymentTypesQuery !== null) {
    initialPaymentTypes = paymentTypesQuery.split(',');
  }

  const initialSearchText = query.get('search_text') ?? '';

  let initialOffset = 0;
  const offsetQuery = query.get('offset');
  if (offsetQuery !== null && !isNaN(parseInt(offsetQuery))) {
    initialOffset = parseInt(offsetQuery);
  }

  let initialDateFrom: Date | null = null;
  const dateFromQuery = query.get('date_from');
  if (dateFromQuery !== null) {
    initialDateFrom = new Date(dateFromQuery);
  } else if (useDefaultDateFilter) {
    const now = new Date();
    // Default to the first day of previous month in local time.
    initialDateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    // As we will convert the date to UTC time when fetching the data, we need to set the hours to 0 and add the timezone offset to local time when UTC is 00:00:00
    initialDateFrom.setHours(0, -now.getTimezoneOffset(), 0, 0);
  }

  let initialDateTo: Date | null = null;
  const dateToQuery = query.get('date_to');
  if (dateToQuery !== null) {
    initialDateTo = new Date(dateToQuery);
  }

  const initialDateRangeValue = [initialDateFrom, initialDateTo];

  return {
    dateRangeValue: initialDateRangeValue,
    status: initialStatus,
    currencies: initialCurrencies,
    paymentTypes: initialPaymentTypes,
    searchText: initialSearchText,
    offset: initialOffset,
  };
};

export const ViewPayments = ({ customerAppId }: { customerAppId: string }): ReactElement => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [hasMore, setHasMore] = useState<boolean | undefined>(undefined);
  const history = useHistory();
  // change the following constant to change the page size
  const pageSize = 100;

  const query = useQuery();
  const location = useLocation<LocationState>();
  const useDefaultDateFilter = location.state?.useDefaultDateFilter ?? true;
  const initialFilters = getInitialFiltersFromQuery(query, useDefaultDateFilter);

  // States for filter UI
  const [dateRangeValue, setDateRangeValue] = useState<DateRangeValues>(initialFilters.dateRangeValue);
  const [status, setStatus] = useState<string[]>(initialFilters.status);
  const [currencies, setCurrencies] = useState<string[]>(initialFilters.currencies);
  const [paymentTypes, setPaymentTypes] = useState<string[]>(initialFilters.paymentTypes);
  const [searchText, setSearchText] = useState(initialFilters.searchText);

  // Fetch data using url params
  const { data, error, isLoading } = ListPayments(customerAppId, {
    statuses: initialFilters.status,
    from: initialFilters.dateRangeValue[0]?.toISOString().slice(0, 10) ?? undefined,
    to: initialFilters.dateRangeValue[1]?.toISOString().slice(0, 10) ?? undefined,
    limit: pageSize,
    offset: initialFilters.offset,
    currencies: initialFilters.currencies,
    paymentTypes: initialFilters.paymentTypes,
    searchText: initialFilters.searchText,
  });

  // for handling changes to data
  useEffect(() => {
    setPayments(data?.payments ?? []);
    setHasMore(data?.has_more);
    if (data) {
      enqueueSnackbar('Loaded payments', { variant: 'info', autoHideDuration: 1000 });
    }
  }, [data, enqueueSnackbar]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  // Prevent out-of-sync state with query params when we update the url parameters via other components (e.g. Clicking the sidebar menu)
  useEffect(() => {
    const filters = getInitialFiltersFromQuery(query, useDefaultDateFilter);
    setDateRangeValue(filters.dateRangeValue);
    setStatus(filters.status);
    setCurrencies(filters.currencies);
    setPaymentTypes(filters.paymentTypes);
    setSearchText(filters.searchText);
  }, [query, useDefaultDateFilter]);

  const handleStatusChange = (event: SelectChangeEvent<typeof statuses>) => {
    const {
      target: { value },
    } = event;
    setStatus(typeof value === 'string' ? value.split(',') : value);
  };

  // Update the url with the filters => fetch data again using the new filters (side effect)
  const updateUrlWithFilters = (filter: paramFilters) => {
    const params = new URLSearchParams();
    if (filter.dateRangeValue[0] !== null) {
      params.set('date_from', filter.dateRangeValue[0].toISOString().slice(0, 10));
    }
    if (filter.dateRangeValue[1] !== null) {
      params.set('date_to', filter.dateRangeValue[1].toISOString().slice(0, 10));
    }
    if (filter.status.length > 0) {
      params.set('status', filter.status.join(','));
    }
    if (filter.currencies.length > 0) {
      params.set('currencies', filter.currencies.join(','));
    }
    if (filter.paymentTypes.length > 0) {
      params.set('payment_types', filter.paymentTypes.join(','));
    }
    if (filter.searchText !== '') {
      // we want to trim trailing spaces that sometimes creep in from copy-pasting text
      params.set('search_text', filter.searchText.trimEnd());
    }

    if (filter.offset !== undefined && filter.offset !== 0) {
      params.set('offset', filter.offset.toString());
    }

    history.replace({
      search: params.toString(),
      state: {
        useDefaultDateFilter: false,
      },
    });
  };

  const goPrev = () => {
    updateUrlWithFilters({
      dateRangeValue,
      status,
      currencies,
      paymentTypes,
      searchText,
      offset: initialFilters.offset - pageSize,
    });
  };

  const goNext = () => {
    updateUrlWithFilters({
      dateRangeValue,
      status,
      currencies,
      paymentTypes,
      searchText,
      offset: initialFilters.offset + pageSize,
    });
  };

  const loadView = () => {
    updateUrlWithFilters({
      dateRangeValue,
      status,
      currencies,
      paymentTypes,
      searchText,
      offset: 0, // reset the filter
    });
  };

  // this function will set the current date time so the UTC value for date string will be the same as the locale time
  // we do this by adding the UTC offset to the date (local), eg 8am in HKT -> 12am in UTC same date.
  // by default the DatePicker component returns the date time as midnight which when converted to UTC will return yesterday's date
  const setDateWithOffset = (newValue: Date) => {
    newValue.getTimezoneOffset(); // get the UTC offset
    newValue.setHours(0); // set the hour to 0 so adding offset will always be the same
    newValue.setMinutes(-newValue.getTimezoneOffset()); // add the offset so we get the local time with UTC offset
  };

  const searchBar = () => {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault(); // we need this to prevent the form from clearing all the inputs
          loadView();
        }}
      >
        <Grid container spacing={2}>
          <Grid container item spacing={2} display="flex" alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="search-bar"
                variant="outlined"
                label="Search"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Search by Amount, External User ID, Finverse User ID, Mandate ID, Metadata values, Payment ID, Payment Method ID, References or Sender Name."
              />
            </Grid>
          </Grid>
          <Grid container item spacing={1} display="flex" justifyContent="start">
            <Grid item xl={2.5} lg={4} md={5} sm={6} xs={12}>
              <DateRangePicker
                values={dateRangeValue}
                views={['day']}
                inputFormat="yyyy-MM-dd"
                onStartDateChange={(newValue: NullableDate) => {
                  if (newValue !== null) {
                    setDateWithOffset(newValue);
                  }
                  setDateRangeValue([newValue, dateRangeValue[1]]);
                }}
                onEndDateChange={(newValue: NullableDate) => {
                  if (newValue !== null) {
                    setDateWithOffset(newValue);
                  }
                  setDateRangeValue([dateRangeValue[0], newValue]);
                }}
                renderInput={(params: TextFieldProps) => (
                  <Box sx={{ width: 200 }}>
                    <TextField
                      fullWidth
                      {...params}
                      sx={{
                        [`& .${outlinedInputClasses.root}:hover > fieldset`]: { borderColor: 'primary.dark' },
                      }}
                    />
                  </Box>
                )}
              />
            </Grid>
            <Grid item xl={2.5} lg={2.5} md={2} sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="select-status-label">Status</InputLabel>
                <Select
                  labelId="select-status-label"
                  id="select-status"
                  multiple
                  value={status}
                  fullWidth
                  onChange={handleStatusChange}
                  renderValue={(selected) => selected.join(', ')}
                  label="Status"
                >
                  {statuses.map((s) => (
                    <MenuItem sx={{ fontSize: '0.8em' }} key={s} value={s} style={getStyles(s, statuses, theme)}>
                      <Checkbox checked={status.includes(s)} />
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xl={2.5} lg={2} md={1.5} sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="currency-select-label">Currency</InputLabel>
                <Select
                  labelId="currency-select-label"
                  id="currency-select"
                  multiple
                  value={currencies}
                  label="Currency"
                  onChange={(e) => {
                    const {
                      target: { value },
                    } = e;
                    setCurrencies(typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(selected) => selected.join(', ')}
                  MenuProps={currencySelectMenuProps}
                >
                  {supportedCurrencies.map((ccy) => (
                    <MenuItem sx={{ fontSize: '0.8em' }} key={ccy} value={ccy}>
                      <Checkbox checked={currencies.includes(ccy)} />
                      {ccy}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xl={2.5} lg={1.5} md={1.5} sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="payment-type-select-label">Payment Type</InputLabel>
                <Select
                  labelId="payment-type-select-label"
                  id="payment-type-select"
                  multiple
                  value={paymentTypes}
                  label="Payment Type"
                  onChange={(e) => {
                    const {
                      target: { value },
                    } = e;
                    setPaymentTypes(typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {paymentTypeOptions.map((pt) => (
                    <MenuItem value={pt.value} sx={{ fontSize: '0.8em' }} key={pt.value}>
                      <Checkbox checked={paymentTypes.includes(pt.value)} />
                      {pt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={2} sm={12}>
              <Button fullWidth variant="contained" sx={{ height: '100%' }} type="submit">
                View
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  };

  const titleBar = () => {
    const from = initialFilters.offset + 1;
    const to = initialFilters.offset + payments.length;

    return (
      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          marginBottom: '1em',
          marginTop: '0.5em',
        }}
      >
        <Grid item>
          <Typography variant="h4" component="h4">
            Payments
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="subtitle1">
            {payments.length > 0 ? `Showing ${from} - ${to} of ${hasMore ? 'many' : to}` : 'No payments found'}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const pagination = () => {
    return (
      <ButtonGroup
        disableElevation
        size="small"
        aria-label="Pagination"
        variant="contained"
        sx={{ width: '250px', marginTop: '1em' }}
      >
        <Button fullWidth disabled={initialFilters.offset === 0} onClick={goPrev}>
          Previous
        </Button>
        <Button fullWidth disabled={!hasMore} onClick={goNext}>
          Next
        </Button>
      </ButtonGroup>
    );
  };

  return (
    <>
      {searchBar()}
      {titleBar()}
      <PaymentsTable customerAppId={customerAppId} items={payments} isLoading={isLoading} />
      {pagination()}
    </>
  );
};
